// SEO Helper Functions for CCTVplanner

/**
 * Structured Data Types for Google Schema
 */

export interface FAQSchema {
  "@context": string;
  "@type": string;
  mainEntity: Array<{
    "@type": string;
    name: string;
    acceptedAnswer: {
      "@type": string;
      text: string;
    };
  }>;
}

export interface HowToSchema {
  "@context": string;
  "@type": string;
  name: string;
  description?: string;
  image?: string;
  step: Array<{
    "@type": string;
    name: string;
    text: string;
    image?: string;
  }>;
}

export interface BreadcrumbSchema {
  "@context": string;
  "@type": string;
  itemListElement: Array<{
    "@type": string;
    position: number;
    name: string;
    item: string;
  }>;
}

/**
 * Generate SoftwareApplication Schema (for homepage and main pages).
 *
 * Emits an `offers` ARRAY covering the Free tier plus every paid
 * subscription tier (Lite, Standard, Premium). Without paid tiers visible
 * to Google, the SERP only ever shows "Free" — the subscriptions never
 * surface in price-tag snippets and conversions suffer. Each paid tier
 * carries a `priceSpecification` of type `UnitPriceSpecification` with
 * `billingDuration: P1M` so Google understands "€X per month".
 *
 * Keep the offers in sync with `PricingPage.tsx` PLANS — divergence here
 * would surface in SERP as the wrong price (a manual-action risk).
 *
 * Pass `aggregateRating` only with real, verifiable numbers sourced from
 * the app_ratings table (see get_aggregate_rating RPC). The rating must
 * also be visibly rendered on the page — Google strips the snippet
 * otherwise.
 */
export function generateSoftwareApplicationSchema(
  aggregateRating?: { avg: number; count: number }
): Record<string, any> {
  const PRICE_VALID_UNTIL = "2027-12-31";

  // Build a monthly subscription Offer with both `price` (top-level so
  // Google's offer parser is happy) and a `priceSpecification` that
  // explicitly says "per month" — without the spec, Google may show the
  // raw price as a one-time fee in SERP.
  const monthlyOffer = (name: string, priceEur: string, sku: string): Record<string, any> => ({
    "@type": "Offer",
    name,
    sku,
    category: "subscription",
    price: priceEur,
    priceCurrency: "EUR",
    availability: "https://schema.org/InStock",
    priceValidUntil: PRICE_VALID_UNTIL,
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: priceEur,
      priceCurrency: "EUR",
      referenceQuantity: {
        "@type": "QuantitativeValue",
        value: 1,
        unitCode: "MON", // UN/CEFACT code for "month"
      },
    },
    url: "https://cctvplanner.io/pricing",
  });

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "CCTVplanner",
    description: "Online CCTV planning and design software for installers",
    url: "https://cctvplanner.io",
    image: "https://cctvplanner.io/og/home.png",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web-based",
    offers: [
      // Free tier — kept first so it remains the headline price in the
      // existing "Free" SERP snippet flow.
      {
        "@type": "Offer",
        name: "Free",
        sku: "cctvplanner-free",
        category: "freemium",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        priceValidUntil: PRICE_VALID_UNTIL,
        url: "https://cctvplanner.io/pricing",
      },
      monthlyOffer("Lite", "2", "cctvplanner-lite-monthly"),
      monthlyOffer("Standard", "5", "cctvplanner-standard-monthly"),
      monthlyOffer("Premium", "10", "cctvplanner-premium-monthly"),
    ],
  };
  if (aggregateRating && aggregateRating.count > 0 && aggregateRating.avg > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: aggregateRating.avg.toFixed(2),
      ratingCount: aggregateRating.count,
      bestRating: "5",
      worstRating: "1",
    };
  }
  return schema;
}

/**
 * Generate FAQ Schema for content pages
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>): FAQSchema {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
}

/**
 * Generate HowTo Schema for tutorial/guide content
 */
export function generateHowToSchema(
  title: string,
  description: string,
  steps: Array<{ name: string; text: string }>
): HowToSchema {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: title,
    description: description,
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      name: step.name,
      text: step.text
    }))
  };
}

/**
 * Generate Breadcrumb Schema
 */
export function generateBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url?: string }>
): BreadcrumbSchema {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((breadcrumb, index) => {
      const item: Record<string, any> = {
        "@type": "ListItem",
        position: index + 1,
        name: breadcrumb.name,
      };
      if (breadcrumb.url) {
        item.item = `https://cctvplanner.io${breadcrumb.url}`;
      }
      return item;
    })
  };
}

/**
 * Generate Organization Schema with founder Person nested.
 *
 * The `founder` property is the single highest-signal claim Google's
 * Knowledge Graph and LLM training pipelines (ChatGPT, Gemini, Perplexity,
 * Claude) use to identify "who is behind X". Without it, LLMs fall back to
 * pattern-matching against any team-page HTML they crawled — which is how
 * the old fictional Jan/Aleksandra/Piotr/Maria team data leaked into ChatGPT
 * training in mid-2025.
 *
 * The founder uses a stable `@id` so the same Person can be cross-referenced
 * from /about and /about/facts ProfilePage / Person blocks.
 *
 * Add new sameAs entries (LinkedIn, X/Twitter) ONLY when the URL is verified
 * — schema.org/sameAs with a dead link tanks the SERP enrichment.
 */
export function generateOrganizationSchema(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://cctvplanner.io/#organization",
    name: "CCTVplanner",
    alternateName: ["CCTV Planner", "CCTVplanner.io"],
    url: "https://cctvplanner.io",
    logo: "https://cctvplanner.io/logo.png",
    description: "Online CCTV planning and design software for installers, integrators, and security professionals. 100% Engineered and Hosted in the EU.",
    foundingDate: "2024",
    foundingLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: "PL",
        addressLocality: "Tczew",
      },
    },
    founder: {
      "@type": "Person",
      "@id": "https://cctvplanner.io/#founder",
      name: "Aleksander Kubiak",
      givenName: "Aleksander",
      familyName: "Kubiak",
      jobTitle: "Founder & Developer",
      nationality: { "@type": "Country", name: "Poland" },
      // alumniOf kept as a plain string (the credential) rather than a nested
      // CollegeOrUniversity node — the node was being emitted sitewide via the
      // Organization/founder block, polluting every page's JSON-LD graph with a
      // stray university entity and confusing entity extraction.
      alumniOf: "Polish Naval Academy in Gdynia",
      worksFor: { "@id": "https://cctvplanner.io/#organization" },
    },
    employee: [{ "@id": "https://cctvplanner.io/#founder" }],
    numberOfEmployees: { "@type": "QuantitativeValue", value: 1 },
    sameAs: [
      // twitter.com/cctvplanner removed 2026-06-21: hard 404 (→ x.com/cctvplanner,
      // also 404). A dead sameAs suppresses entity enrichment (see note above).
      "https://www.facebook.com/cctvplanner",
      "https://www.linkedin.com/company/cctvplanner",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Support",
      email: "contact@cctvplanner.io",
      availableLanguage: ["en", "pl", "de", "pt", "es", "hi", "id", "fil", "fr", "it", "ja", "ko", "ar", "tr", "vi", "th", "uk", "cs", "nl", "hu", "lt", "ro", "sv"],
    },
  };
}

/**
 * Standalone Person schema for the founder. Emit on /about and /about/facts
 * as a root-level node (not just nested inside Organization) so search-
 * augmented LLMs index the Person as a first-class entity rather than as
 * a sub-property they may strip during summarization.
 *
 * Shares `@id` with the Organization.founder reference — JSON-LD parsers
 * merge nodes that share an IRI, so the same Person carries Organization
 * context AND its own ProfilePage/About metadata.
 */
export function generateFounderPersonSchema(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://cctvplanner.io/#founder",
    name: "Aleksander Kubiak",
    givenName: "Aleksander",
    familyName: "Kubiak",
    jobTitle: "Founder & Developer of CCTVplanner.io",
    description: "Polish security engineer and self-taught developer. Sole founder and sole developer of CCTVplanner.io. Graduate of the Polish Naval Academy in Gdynia (internal security specialisation: facility protection, risk analysis, security systems). Ten years of hands-on CCTV system design experience prior to building CCTVplanner.",
    nationality: { "@type": "Country", name: "Poland" },
    knowsLanguage: ["pl", "en"],
    // Plain-string alumniOf — same fix as the Organization founder block; this
    // Person shares its @id with that block, so a divergent nested node here
    // would re-introduce the stray CollegeOrUniversity entity on merge.
    alumniOf: "Polish Naval Academy in Gdynia",
    knowsAbout: [
      "CCTV system design",
      "EN 62676-4",
      "IEC 62676-4:2025",
      "DORI",
      "OODPCVS",
      "EN 50131",
      "PoE network design",
      "GDPR-compliant video surveillance",
    ],
    worksFor: { "@id": "https://cctvplanner.io/#organization" },
    url: "https://cctvplanner.io/about",
  };
}

/**
 * Generate WebSite Schema (for sitelinks + brand SERP).
 * Emit on every page so Google has site-wide context for sitelinks.
 */
export function generateWebSiteSchema(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://cctvplanner.io/#website",
    name: "CCTVplanner",
    alternateName: ["CCTV Planner", "CCTVplanner.io"],
    url: "https://cctvplanner.io",
    description: "Free online CCTV planning and design software. Browser-based, 65,000+ camera models, DORI per EN 62676-4, 100% EU-hosted.",
    inLanguage: ["en", "pl", "de", "es", "fr", "it", "pt-PT", "pt-BR", "ja", "ko", "hi", "ar", "tr", "vi", "th", "fil", "id", "uk", "cs", "nl", "hu", "lt", "ro", "sv"],
    publisher: {
      "@type": "Organization",
      name: "CCTVplanner",
      url: "https://cctvplanner.io",
      logo: "https://cctvplanner.io/logo.png"
    },
    // Sitelinks searchbox — folded in from the old hand-coded homepage WebSite
    // block (now removed from Index.tsx) so it ships site-wide, not just on /.
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://cctvplanner.io/cameras?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };
}

/**
 * Generate LocalBusiness Schema
 */
export function generateLocalBusinessSchema(): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "DEFENSAR - CCTVplanner",
    image: "https://cctvplanner.io/logo.png",
    description: "Professional CCTV planning and design software",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Zamkowa 20A/21",
      addressLocality: "Tczew",
      postalCode: "83-110",
      addressCountry: "PL"
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Sales",
      email: "contact@cctvplanner.io"
    },
    priceRange: "$"
  };
}

/**
 * Generate Product/Tool Schema for Calculators
 */
export function generateToolSchema(
  name: string,
  description: string,
  url: string
): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: name,
    description: description,
    url: url,
    applicationCategory: "CalculatorApplication"
  };
}

/**
 * Generate schema.org Brand JSON-LD for a camera brand.
 *
 * Used on /compare/<brandA>-vs-<brandB> pages and other brand-faceted
 * landing pages. LLMs ingesting the page get a structured "this is the
 * Hikvision brand, HQ China, NDAA-banned, ~19,500 SKUs" fact unit they
 * can extract verbatim.
 *
 * `aggregateRating` is omitted intentionally — we don't have verifiable
 * brand-level ratings and Google strips structured data containing
 * unsubstantiated ratings.
 */
export function generateBrandSchema(config: {
  name: string;
  slug: string;
  hqCountry?: string;
  founded?: number;
  description?: string;
  url?: string;
  logo?: string;
}): Record<string, any> {
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Brand",
    "@id": `https://cctvplanner.io/cameras#brand-${config.slug}`,
    name: config.name,
    url: config.url || `https://cctvplanner.io/cameras?brand=${config.slug}`,
  };
  if (config.description) schema.description = config.description;
  if (config.logo) schema.logo = config.logo;
  if (config.hqCountry || config.founded) {
    schema.manufacturer = {
      "@type": "Organization",
      name: config.name,
      ...(config.hqCountry && {
        address: { "@type": "PostalAddress", addressCountry: config.hqCountry },
      }),
      ...(config.founded && { foundingDate: String(config.founded) }),
    };
  }
  return schema;
}

/**
 * Generate Article schema for a comparison page, referencing two Brands.
 *
 * Used on brand-vs-brand comparison pages so LLMs and rich-result engines
 * recognise the page as a structured comparison with two named entities
 * rather than a free-form blog post.
 */
export function generateComparisonArticleSchema(config: {
  headline: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  brandASlug: string;
  brandAName: string;
  brandBSlug: string;
  brandBName: string;
}): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": config.url,
    headline: config.headline,
    description: config.description,
    url: config.url,
    datePublished: config.datePublished,
    dateModified: config.dateModified || config.datePublished,
    about: [
      {
        "@type": "Brand",
        "@id": `https://cctvplanner.io/cameras#brand-${config.brandASlug}`,
        name: config.brandAName,
      },
      {
        "@type": "Brand",
        "@id": `https://cctvplanner.io/cameras#brand-${config.brandBSlug}`,
        name: config.brandBName,
      },
    ],
    author: {
      "@type": "Organization",
      name: "CCTVplanner",
      url: "https://cctvplanner.io",
    },
    publisher: {
      "@type": "Organization",
      name: "CCTVplanner",
      url: "https://cctvplanner.io",
      logo: {
        "@type": "ImageObject",
        url: "https://cctvplanner.io/logo.png",
      },
    },
    // ItemList — the actual comparison as a machine-readable list. Google's
    // comparison rich result (and AI Overviews comparison cards) require
    // ItemList with two Product/Brand entries. Article alone is editorial
    // content; ItemList tells Google "this page IS the comparison."
    mentions: [
      {
        "@type": "ItemList",
        name: `${config.brandAName} vs ${config.brandBName}`,
        itemListOrder: "https://schema.org/ItemListUnordered",
        numberOfItems: 2,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "Brand",
              "@id": `https://cctvplanner.io/cameras#brand-${config.brandASlug}`,
              name: config.brandAName,
              url: `https://cctvplanner.io/brand/${config.brandASlug}`,
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@type": "Brand",
              "@id": `https://cctvplanner.io/cameras#brand-${config.brandBSlug}`,
              name: config.brandBName,
              url: `https://cctvplanner.io/brand/${config.brandBSlug}`,
            },
          },
        ],
      },
    ],
    mainEntityOfPage: { "@type": "WebPage", "@id": config.url },
  };
}

/**
 * Generate schema.org DefinedTermSet JSON-LD for a glossary.
 *
 * Each term entry becomes a DefinedTerm child. LLMs use these as
 * single-source-of-truth definitions for technical vocabulary (DORI,
 * OODPCVS, PoE, NDAA, etc.) and cite them in domain-specific answers.
 */
export function generateDefinedTermSetSchema(config: {
  name: string;
  description: string;
  url: string;
  terms: Array<{ term: string; definition: string; url?: string }>;
}): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": config.url,
    name: config.name,
    description: config.description,
    url: config.url,
    hasDefinedTerm: config.terms.map(t => ({
      "@type": "DefinedTerm",
      "@id": `${config.url}#${t.term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      name: t.term,
      description: t.definition,
      inDefinedTermSet: config.url,
      ...(t.url && { url: t.url }),
    })),
  };
}

/**
 * Generate Image Schema
 */
export function generateImageSchema(
  url: string,
  altText: string,
  width?: number,
  height?: number
): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    url: url,
    name: altText,
    description: altText,
    ...(width && { width: width }),
    ...(height && { height: height })
  };
}

/**
 * Generate BlogPosting Schema for blog articles.
 *
 * `inLanguage` accepts either undefined (English default) or a language
 * prefix from LANG_PREFIXES. Most prefixes are already valid BCP-47 codes,
 * but the regional Portuguese variants are normalized to canonical
 * region-uppercase form (`pt-br` → `pt-BR`) since Google's structured-data
 * validators are stricter than our internal URL prefixes.
 */
function normalizeBcp47(lang: string | undefined): string {
  if (!lang) return "en";
  if (lang === "pt-br") return "pt-BR";
  if (lang === "pt-pt") return "pt-PT";
  return lang;
}

export function generateBlogPostingSchema(config: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  image?: string;
  slug: string;
  inLanguage?: string;
}): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": config.title,
    "description": config.description,
    "url": config.url,
    "datePublished": config.datePublished,
    "dateModified": config.dateModified || config.datePublished,
    // Author = Person (founder) not Organization. E-E-A-T signal for AIO.
    // The full founder Person schema with alumniOf / sameAs / etc. is emitted
    // by generateFounderPersonSchema() on the homepage; here we reference it
    // via @id so duplication is avoided while still tying every blog post to
    // a named, credentialed author.
    "author": {
      "@type": "Person",
      "@id": "https://cctvplanner.io/#founder",
      "name": "Aleksander Kubiak",
      "url": "https://cctvplanner.io/about",
      "jobTitle": "Founder & Developer",
      // Plain-string alumniOf — matches the founder Person/@id block; avoids
      // re-emitting the stray CollegeOrUniversity node on every blog post.
      "alumniOf": "Polish Naval Academy in Gdynia"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CCTVplanner",
      "url": "https://cctvplanner.io",
      "logo": {
        "@type": "ImageObject",
        "url": "https://cctvplanner.io/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": config.url
    },
    ...(config.image && {
      "image": {
        "@type": "ImageObject",
        "url": config.image,
        "width": 1200,
        "height": 630
      }
    }),
    "inLanguage": normalizeBcp47(config.inLanguage),
    "isPartOf": {
      "@type": "Blog",
      "name": "CCTVplanner Blog",
      "url": "https://cctvplanner.io/blog"
    }
  };
}

/**
 * Insert schema markup into document head
 * Only creates one script tag per schema type to avoid duplication
 */
export function injectSchemaMarkup(schema: Record<string, any>, id?: string): void {
  // Prevent creating duplicate scripts for same schema type
  const schemaType = id || schema["@type"];
  const existingScript = document.querySelector(`script[data-schema-type="${schemaType}"]`);

  const script = existingScript || document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-schema-type", schemaType);
  try {
    script.textContent = JSON.stringify(schema);
  } catch (e) {
    console.warn("Failed to serialize schema markup:", e);
    return;
  }

  if (!existingScript) {
    document.head.appendChild(script);
  }
}

/**
 * Update document meta tags
 * Optimized to batch DOM queries and only update when needed
 */
export interface MetaTagsConfig {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  canonicalUrl?: string;
  twitterHandle?: string;
  language?: string;
  alternateLanguages?: Array<{ lang: string; url: string }>;
}

export function updateMetaTags(config: MetaTagsConfig): void {
  // Only update if title actually changed
  if (document.title !== config.title) {
    document.title = config.title;
  }

  // Update or create description meta tag
  let descMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement;
  if (!descMeta) {
    descMeta = document.createElement("meta");
    descMeta.setAttribute("name", "description");
    document.head.appendChild(descMeta);
  }
  if (descMeta.content !== config.description) {
    descMeta.content = config.description;
  }

  // Keywords meta intentionally NOT emitted — Google ignores since 2009.
  // The config.keywords field is kept in MetaTagsConfig for backwards-compat
  // with existing call sites, but no DOM tag is created. Remove call-site
  // `keywords:` props in a future cleanup pass.

  // Update OG title
  if (config.ogTitle) {
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (!ogTitle) {
      ogTitle = document.createElement("meta");
      ogTitle.setAttribute("property", "og:title");
      document.head.appendChild(ogTitle);
    }
    if (ogTitle.content !== config.ogTitle) {
      ogTitle.content = config.ogTitle;
    }
  }

  // Update OG description
  if (config.ogDescription) {
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement;
    if (!ogDesc) {
      ogDesc = document.createElement("meta");
      ogDesc.setAttribute("property", "og:description");
      document.head.appendChild(ogDesc);
    }
    if (ogDesc.content !== config.ogDescription) {
      ogDesc.content = config.ogDescription;
    }
  }

  // Update OG image
  if (config.ogImage) {
    let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
    if (!ogImage) {
      ogImage = document.createElement("meta");
      ogImage.setAttribute("property", "og:image");
      document.head.appendChild(ogImage);
    }
    if (ogImage.content !== config.ogImage) {
      ogImage.content = config.ogImage;
    }

    // Add og:image:width and og:image:height for better previews
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]') as HTMLMetaElement;
    if (!ogImageWidth) {
      ogImageWidth = document.createElement("meta");
      ogImageWidth.setAttribute("property", "og:image:width");
      ogImageWidth.content = "1200";
      document.head.appendChild(ogImageWidth);
    }

    let ogImageHeight = document.querySelector('meta[property="og:image:height"]') as HTMLMetaElement;
    if (!ogImageHeight) {
      ogImageHeight = document.createElement("meta");
      ogImageHeight.setAttribute("property", "og:image:height");
      ogImageHeight.content = "630";
      document.head.appendChild(ogImageHeight);
    }
  }

  // Update og:type
  if (config.ogType) {
    let ogType = document.querySelector('meta[property="og:type"]') as HTMLMetaElement;
    if (!ogType) {
      ogType = document.createElement("meta");
      ogType.setAttribute("property", "og:type");
      document.head.appendChild(ogType);
    }
    if (ogType.content !== config.ogType) {
      ogType.content = config.ogType;
    }
  }

  // Add Twitter Card tags
  if (config.twitterHandle) {
    let twitterCard = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement;
    if (!twitterCard) {
      twitterCard = document.createElement("meta");
      twitterCard.setAttribute("name", "twitter:card");
      twitterCard.content = "summary_large_image";
      document.head.appendChild(twitterCard);
    }

    let twitterHandle = document.querySelector('meta[name="twitter:creator"]') as HTMLMetaElement;
    if (!twitterHandle) {
      twitterHandle = document.createElement("meta");
      twitterHandle.setAttribute("name", "twitter:creator");
      twitterHandle.content = config.twitterHandle;
      document.head.appendChild(twitterHandle);
    }

    let twitterTitle = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement;
    if (!twitterTitle) {
      twitterTitle = document.createElement("meta");
      twitterTitle.setAttribute("name", "twitter:title");
      document.head.appendChild(twitterTitle);
    }
    if (twitterTitle.content !== config.title) {
      twitterTitle.content = config.title;
    }

    let twitterDesc = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement;
    if (!twitterDesc) {
      twitterDesc = document.createElement("meta");
      twitterDesc.setAttribute("name", "twitter:description");
      document.head.appendChild(twitterDesc);
    }
    if (twitterDesc.content !== config.description) {
      twitterDesc.content = config.description;
    }

    if (config.ogImage) {
      let twitterImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement;
      if (!twitterImage) {
        twitterImage = document.createElement("meta");
        twitterImage.setAttribute("name", "twitter:image");
        document.head.appendChild(twitterImage);
      }
      if (twitterImage.content !== config.ogImage) {
        twitterImage.content = config.ogImage;
      }
    }
  }

  // Add canonical URL if provided
  // NOTE: Canonical URL handling intentionally disabled here.
  // Canonical tags are managed exclusively by <HreflangTags> in RootLayout
  // (via react-helmet-async during SSG) and by ssg-postprocess.mjs after build.
  // Individual pages should NOT override the canonical, as it breaks
  // language-prefixed routes (/pl, /es, /it...) by pointing them to the
  // English-only URL hardcoded in the page component.
  // The `config.canonicalUrl` field is kept for backwards compatibility
  // but is no longer written to the DOM.

  // Add hreflang tags for alternate language versions
  if (config.alternateLanguages && config.alternateLanguages.length > 0) {
    // Remove old hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => {
      if (!config.alternateLanguages?.some(alt => alt.url === el.getAttribute('href'))) {
        el.remove();
      }
    });

    // Add new hreflang tags
    config.alternateLanguages.forEach(alt => {
      let hreflang = document.querySelector(`link[rel="alternate"][hreflang="${alt.lang}"]`) as HTMLLinkElement;
      if (!hreflang) {
        hreflang = document.createElement("link");
        hreflang.setAttribute("rel", "alternate");
        hreflang.setAttribute("hreflang", alt.lang);
        hreflang.href = alt.url;
        document.head.appendChild(hreflang);
      } else if (hreflang.href !== alt.url) {
        hreflang.href = alt.url;
      }
    });

    // Add x-default hreflang for English
    if (config.canonicalUrl && config.alternateLanguages && !config.alternateLanguages.some(alt => alt.lang === 'x-default')) {
      let xDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]') as HTMLLinkElement;
      if (!xDefault) {
        xDefault = document.createElement("link");
        xDefault.setAttribute("rel", "alternate");
        xDefault.setAttribute("hreflang", "x-default");
        xDefault.href = config.canonicalUrl.replace(/\/[a-z]{2}(-[a-z]{2})?\//, '/');
        document.head.appendChild(xDefault);
      }
    }
  }
}

/**
 * React component that renders JSON-LD schema as a real <script> tag so
 * it is present in SSG-prerendered HTML (what Googlebot sees on the first
 * crawl, before running any JavaScript).
 *
 * Usage:
 *   import { SchemaScript } from '@/lib/seoHelpers';
 *   <SchemaScript schema={generateFAQSchema(...)} />
 *   <SchemaScript schema={generateBreadcrumbSchema(...)} />
 */
import React from 'react';

export function SchemaScript({ schema }: { schema: Record<string, any> }): React.ReactElement | null {
  // Escape `<` to prevent an accidental `</script>` inside the JSON payload
  // from closing the wrapping <script> tag prematurely.
  const json = JSON.stringify(schema).replace(/</g, '\\u003c');
  // createElement (not JSX) because this file is .ts, not .tsx.
  return React.createElement('script', {
    type: 'application/ld+json',
    dangerouslySetInnerHTML: { __html: json },
  });
}

/**
 * Meta title templates
 */
export const metaTitleTemplates = {
  homepage: "CCTV Planning Software | Design Layouts in Minutes | CCTVplanner",
  features: "Features | Professional CCTV Design Tool | CCTVplanner",
  pricing: "Pricing | CCTV Planning Software | CCTVplanner",
  blog: (title: string) => `${title}: Complete Guide ${new Date().getFullYear()} | CCTVplanner Blog`,
  calculator: (name: string) => `${name} | Free Online Tool | CCTVplanner`,
  useCase: (vertical: string) => `${vertical} CCTV Layout & Camera Placement Guide | CCTVplanner`,
  guide: (topic: string) => `${topic}: Complete CCTV Design Guide ${new Date().getFullYear()} | CCTVplanner`,
};

/**
 * Meta description templates
 */
export const metaDescriptionTemplates = {
  homepage:
    "Design professional CCTV layouts in minutes. Free field of view calculator, camera placement guide, and planning software for installers & contractors.",
  features:
    "Explore CCTVplanner features: FOV calculator, camera placement tool, coverage maps, system comparison, and PDF exports. Perfect for installers.",
  calculator: (name: string) =>
    `Free ${name}. Calculate instantly. Used by installers worldwide. No credit card required. Try it free.`,
  useCase: (vertical: string) =>
    `${vertical} CCTV layout guide: camera placement, coverage, blind spots, design tips. See example layouts and calculators for ${vertical}.`,
};

/**
 * Common FAQ schema data by topic
 */
export const commonFAQs = {
  cctvDesign: [
    {
      question: "What is field of view in CCTV?",
      answer:
        "Field of view (FOV) is the area that a camera can capture from its position. It's determined by the lens focal length and sensor size. A wider FOV (like 2-8mm lens) captures more area but with less detail, while a narrower FOV (like 12-50mm) captures less area but with more detail at distance."
    },
    {
      question: "How do I calculate how many cameras I need?",
      answer:
        "Calculate cameras needed by dividing your space into coverage zones. Each camera typically covers 10-20 square meters depending on the lens and required detail level. Add extra cameras for entry/exit points and blind spot areas. Use our camera quantity calculator for precise estimates."
    },
    {
      question: "What is DORI in CCTV systems?",
      answer:
        "DORI stands for Detection, Observation, Recognition, Identification - the four levels of CCTV performance. Detection: see movement. Observation: see actions. Recognition: identify person/vehicle type. Identification: read faces/license plates. Different lens/megapixel combinations achieve different DORI levels."
    },
    {
      question: "How do I eliminate blind spots?",
      answer:
        "Blind spots occur where camera coverage doesn't overlap. To eliminate them: 1) Map your space precisely, 2) Position cameras to overlap coverage, 3) Use wide-angle lenses for general coverage and telephoto for specific areas, 4) Consider camera height and angle, 5) Test your layout virtually before installation."
    }
  ],
  calculators: [
    {
      question: "Why is field of view important?",
      answer:
        "FOV determines how much area your camera covers and how detailed the image will be at distance. Understanding FOV helps you choose the right lens for each camera position and ensures you achieve the required level of detail (DORI) for your application."
    },
    {
      question: "How does megapixel affect distance coverage?",
      answer:
        "Higher megapixels allow you to capture more detail at distance. A 4MP camera can identify people at ~15m, while an 8MP camera at ~30m. The relationship isn't linear - use our megapixel distance calculator to find the right combination for your needs."
    }
  ],
  useCases: [
    {
      question: "Can I use the same design for different building types?",
      answer:
        "While principles are similar, different building types have unique requirements. Warehouses need overhead coverage and loading dock monitoring. Retail needs POS and entrance focus. Parking lots need night vision and space coverage. Use our vertical-specific guides for optimal designs."
    }
  ]
};

/**
 * Generate structured breadcrumb navigation
 */
export function getBreadcrumbs(path: string): Array<{ name: string; url: string }> {
  const breadcrumbs: Record<string, Array<{ name: string; url: string }>> = {
    "/": [{ name: "Home", url: "/" }],
    "/features": [
      { name: "Home", url: "/" },
      { name: "Features", url: "/features" }
    ],
    "/pricing": [
      { name: "Home", url: "/" },
      { name: "Pricing", url: "/pricing" }
    ],
    "/how-it-works": [
      { name: "Home", url: "/" },
      { name: "How It Works", url: "/how-it-works" }
    ],
    "/use-cases": [
      { name: "Home", url: "/" },
      { name: "Use Cases", url: "/use-cases" }
    ],
    "/blog": [
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" }
    ],
    "/resources": [
      { name: "Home", url: "/" },
      { name: "Resources", url: "/resources" }
    ],
    "/calculators": [
      { name: "Home", url: "/" },
      { name: "Calculators", url: "/calculators" }
    ]
  };

  return breadcrumbs[path] || [{ name: "Home", url: "/" }];
}

/**
 * Sitemap generator helper
 */
export interface SitemapEntry {
  loc: string;
  lastmod?: string;
  changefreq?: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
  priority?: number;
}

function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

export function generateSitemapXML(entries: SitemapEntry[]): string {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

  entries.forEach(entry => {
    xml += "  <url>\n";
    xml += `    <loc>${escapeXml(entry.loc)}</loc>\n`;
    if (entry.lastmod) {
      xml += `    <lastmod>${escapeXml(entry.lastmod)}</lastmod>\n`;
    }
    if (entry.changefreq) {
      xml += `    <changefreq>${escapeXml(entry.changefreq)}</changefreq>\n`;
    }
    if (entry.priority !== undefined) {
      xml += `    <priority>${entry.priority}</priority>\n`;
    }
    xml += "  </url>\n";
  });

  xml += "</urlset>";
  return xml;
}
